import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Badge, Table, Accordion, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Invoice, InvoiceStatus, EstimateStatus, PayingClient } from '../types/invoiceType';
import ImagePreview from './ImagePreview';
import TipSelection from './TipSelection';
import axios from 'axios';
import { Config } from '../config';

const DisplayInvoice = (props: { invoiceId: string, setInvoice: any, totalDue?: number, forceShowSignedDownload?: boolean, onRelatedInvoicesLoaded?: (invoices: Invoice[]) => void }) => {
    const [invoice, setInvoice] = React.useState<Invoice | null>(null);
    const [customer, setCustomer] = useState<PayingClient | null>(null);
    const [relatedInvoices, setRelatedInvoices] = useState<Invoice[]>([]);
    // State for tip management
    const [tipAmount, setTipAmount] = useState<number>(0);
    const [tipPercent, setTipPercent] = useState<number | null>(null);
    const navigate = useNavigate();
    const invoiceId = props.invoiceId;

    // Backend API base URL
    const API_BASE_URL = Config.Env.ApiBaseUrl;

    // Note: Related invoices are now fetched from the backend API along with the main document

    useEffect(() => {
        console.log("Related invoices:", relatedInvoices);
    }, [relatedInvoices])

    useEffect(() => {
        const getInvoice = async () => {
            try {
                // Fetch invoice/estimate from backend API
                const response = await axios.get(`${API_BASE_URL}/invoices/${invoiceId}`);

                if (response.data.status === 'success') {
                    const { document, relatedInvoices: related, customer: customerData } = response.data.data;

                    // Convert document data to Invoice format
                    const isEstimate = document.documentType === 'estimate';
                    const invoiceData: Invoice = {
                        ...document,
                        id: invoiceId,
                        isEstimate: isEstimate,
                        amount: document.totalPrice || document.amount || 0,
                        fullName: document.customerName || document.fullName || '',
                        title: document.title || (document.selectedServices && Object.values(document.selectedServices).some((service: any) =>
                            service.type?.toLowerCase().includes('solar') ||
                            service.name?.toLowerCase().includes('solar')
                        ) ? "Solar Screen Estimate" : "Estimate"),
                        address: document.address || "",
                        email: document.email || "",
                        phoneNumber: document.phoneNumber || "",
                        message: document.notes || document.message || "Thank you for choosing us for your needs.",
                        images: document.images || []
                    } as Invoice;

                    setInvoice(invoiceData);
                    // Include tip information when setting invoice
                    const invoiceWithTip = {
                        ...invoiceData,
                        tipAmount: invoiceData.tipAmount || 0,
                        tipPercent: invoiceData.tipPercent || null
                    };
                    props.setInvoice(invoiceWithTip);

                    // Initialize tip state if tip exists
                    if (invoiceData.tipAmount) {
                        setTipAmount(invoiceData.tipAmount);
                        setTipPercent(invoiceData.tipPercent || null);
                    }

                    // Debug logging
                    console.log("Invoice data:", {
                        amount: invoiceData.amount,
                        totalPrice: invoiceData.totalPrice,
                        total: invoiceData.total,
                        discountedTotal: invoiceData.discountedTotal,
                        getTotalPrice: getTotalPrice(invoiceData),
                        meshType: invoiceData.meshType
                    });

                    // Set customer data if available
                    if (customerData) {
                        setCustomer(customerData as PayingClient);
                    }

                    // Set related invoices if available
                    if (related && related.length > 0) {
                        setRelatedInvoices(related);
                        props.onRelatedInvoicesLoaded?.(related);
                    }
                }
            } catch (error: any) {
                console.error("Error fetching invoice/estimate:", error);
                if (axios.isAxiosError(error) && error.response?.status === 404) {
                    console.log("No such document in estimates or invoices collections!");
                } else {
                    console.error("Unexpected error:", error);
                }
            }
        };

        getInvoice();
    }, [invoiceId, API_BASE_URL]);

    const getTotalPrice = (invoice: Invoice) => {
        if (invoice.totalPrice) {
            return invoice.totalPrice;
        } else if (invoice.total) {
            return invoice.total;
        } else if (invoice.amount) {
            return invoice.amount;
        } else {
            return 0;
        }
    }

    const formatCurrency = (value: number) => {
        return `$${value.toFixed(2).replace(/\.00$/, '')}`;
    };

    return (
        <Row>
            {invoice ? (
                <Card className="shadow rounded-lg overflow-hidden">

                    <Card.Body className={invoice.isEstimate ? 'py-4 estimate-details' : 'p-4'}>
                        {/* Title Section */}
                        <div className="mb-4 pb-2">
                            <h4 className="text-dark">{invoice.title}</h4>
                        </div>

                        {/* Customer Information */}
                        <div className="mb-4">
                            <div className="bg-light p-3 rounded-lg">
                                <h5 className="pb-2 mb-3 text-dark">
                                    <i className="fas fa-user-circle me-2"></i>
                                    Customer Details
                                </h5>
                                <div className="d-flex flex-column">
                                    {/* Show customer data from clients collection if available */}
                                    {customer ? (
                                        <>
                                            <p className="mb-2"><strong>Name:</strong> {customer.firstName} {customer.lastName || ''}</p>
                                            <p className="mb-2"><strong>Email:</strong> {customer.email || 'Not provided'}</p>
                                            <p className="mb-2"><strong>Phone:</strong> {customer.phoneNumber || 'Not provided'}</p>
                                            <p className="mb-2"><strong>Address:</strong> {customer.address || invoice.address}</p>
                                            <p className="mb-0"><strong>Zip Code:</strong> {customer.zipCode}</p>
                                            {customer.howCustomerFoundUs && (
                                                <div className="mt-3 pt-2 text-muted small">
                                                    <em>Found us via: {customer.howCustomerFoundUs}</em>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        // Fallback to invoice data if customer data isn't available
                                        <>
                                            <p className="mb-2"><strong>Name:</strong> {invoice.fullName}</p>
                                            <p className="mb-2"><strong>Address:</strong> {invoice.address}</p>
                                            {invoice.email && (
                                                <p className="mb-2"><strong>Email:</strong> {invoice.email}</p>
                                            )}
                                            {invoice.phoneNumber && (
                                                <p className="mb-0"><strong>Phone:</strong> {invoice.phoneNumber}</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Message Section */}
                        <div className="mb-4">
                            <div className="bg-light p-3 rounded-lg">
                                <h5 className="pb-2 mb-3 text-dark">
                                    <i className="fas fa-comment-alt me-2"></i>
                                    Message
                                </h5>
                                <p className="text-muted mb-0">{invoice.message}</p>
                            </div>
                        </div>

                        {/* Line Items Section */}
                        <div className="mb-4">
                            <div className="card no-section-border">
                                <div className="card-header work-items-header">
                                    <h5 className="mb-0">
                                        <i className="fas fa-clipboard-list me-2"></i>
                                        Work Items
                                    </h5>
                                </div>
                                <div className="card-body p-0">
                                    {/* Solar Screen items from estimate - using selectedServices */}
                                    {invoice.selectedServices && Object.keys(invoice.selectedServices).length > 0 && (
                                        <div className="table-responsive">
                                            <Table hover className="mb-0 work-items-table">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th style={{ width: '5%' }}>#</th>
                                                        <th style={{ width: '35%' }}>Item</th>
                                                        <th style={{ width: '25%' }}>
                                                            {Object.values(invoice.selectedServices).some((service: any) =>
                                                                service.type?.toLowerCase().includes('solar') ||
                                                                service.name?.toLowerCase().includes('solar')
                                                            ) ? 'Solar Screen Dimensions' : 'Dimensions'}
                                                        </th>
                                                        <th style={{ width: '35%' }} className="text-end">Price</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Object.entries(invoice.selectedServices).map(([key, service]: [string, any], index: number) => {
                                                        // Extract dimensions
                                                        const dimensions = service.dimensions;

                                                        // Extract width values with fallbacks
                                                        const width = dimensions?.width?.whole || 0;
                                                        const widthTop = dimensions?.width?.top || '';
                                                        const widthBottom = dimensions?.width?.bottom || '';

                                                        // Extract length values with fallbacks
                                                        const length = dimensions?.length?.whole || 0;
                                                        const lengthTop = dimensions?.length?.top || '';
                                                        const lengthBottom = dimensions?.length?.bottom || '';

                                                        // Format dimensions display
                                                        const widthDisplay = width + (widthTop && widthBottom ? ` ${widthTop}/${widthBottom}` : '');
                                                        const lengthDisplay = length + (lengthTop && lengthBottom ? ` ${lengthTop}/${lengthBottom}` : '');

                                                        // Get price directly from service
                                                        const price = service.price || 0;

                                                        // Get service name
                                                        const serviceName = service.name || key;

                                                        // Get notes from dimensions
                                                        const itemNotes = service.dimensions?.notes;

                                                        return (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td>
                                                                    <strong>{serviceName}</strong>
                                                                    {itemNotes && (
                                                                        <div className="text-muted small mt-1">
                                                                            <em>{itemNotes}</em>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>{widthDisplay && lengthDisplay ? `${widthDisplay}" × ${lengthDisplay}"` : 'N/A'}</td>
                                                                <td className="text-end">
                                                                    <strong>${Math.round(price * (invoice.meshType === 80 ? .88 : 1))}</strong>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}

                                    {/* Simple services without line items */}
                                    {(!invoice.selectedServices || Object.keys(invoice.selectedServices).length === 0) && invoice.lineItems === undefined && invoice.servicesDone && invoice.servicesDone.length > 0 && (
                                        <div className="table-responsive">
                                            <Table hover className="mb-0 work-items-table">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th style={{ width: '5%' }}>#</th>
                                                        <th style={{ width: '65%' }}>Service</th>
                                                        <th style={{ width: '30%' }} className="text-end">Price</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoice.servicesDone.map((service, index) => {
                                                        // Get price from selectedServices if available
                                                        const selectedService = invoice.selectedServices && invoice.selectedServices[service];
                                                        const price = selectedService && selectedService.price
                                                            ? Math.round(selectedService.price)
                                                            : invoice.servicesDone?.length ?? 0 > 0
                                                                ? Math.round(invoice.amount / (invoice.servicesDone?.length ?? 0))
                                                                : 0;

                                                        // Get dimensions from selectedServices if available
                                                        const dimensions = selectedService && selectedService.dimensions;
                                                        const lengthWhole = dimensions?.length?.whole || '';
                                                        const lengthTop = dimensions?.length?.top || '';
                                                        const lengthBottom = dimensions?.length?.bottom || '';
                                                        const widthWhole = dimensions?.width?.whole || '';
                                                        const widthTop = dimensions?.width?.top || '';
                                                        const widthBottom = dimensions?.width?.bottom || '';

                                                        // Format dimensions display
                                                        const length = lengthWhole + (lengthTop && lengthBottom ? ` ${lengthTop}/${lengthBottom}` : '');
                                                        const width = widthWhole + (widthTop && widthBottom ? ` ${widthTop}/${widthBottom}` : '');
                                                        const dimensionText = length && width ? `${width}" × ${length}"` : '';

                                                        return (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td>
                                                                    {selectedService?.name || service}
                                                                    {dimensionText && (
                                                                        <div className="text-muted small mt-1">
                                                                            Dimensions: {dimensionText}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="text-end">
                                                                    <strong>${price}</strong>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}

                                    {/* Detailed line items */}
                                    {!invoice.solarScreens &&
                                        (!invoice.selectedServices || Object.keys(invoice.selectedServices).length === 0) &&
                                        (!invoice.servicesDone || invoice.servicesDone.length === 0) &&
                                        invoice.lineItems !== undefined &&
                                        invoice.lineItems.length > 0 && (
                                            <div className="table-responsive">
                                                <Table hover className="mb-0 work-items-table">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th style={{ width: '5%' }}>#</th>
                                                            <th style={{ width: '55%' }}>Item</th>
                                                            <th style={{ width: '15%' }} className="text-center">Qty</th>
                                                            <th style={{ width: '25%' }} className="text-end">Price</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {invoice.lineItems.map((item: any, index: number) => (
                                                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-light'}>
                                                                <td>{index + 1}</td>
                                                                <td>
                                                                    <strong>{item.name}</strong>
                                                                    {item.description && (
                                                                        <div className="text-muted small mt-1">{item.description}</div>
                                                                    )}
                                                                </td>
                                                                <td className="text-center">{item.quantity || 1}</td>
                                                                <td className="text-end">
                                                                    <strong>${Math.round(item.unitPrice)}</strong>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>

                        {/* Solar Screen Colors Section (only for estimates with mesh/frame colors and dealing with solar screens) */}
                        {invoice.isEstimate && (invoice.meshColor || invoice.frameColor) &&
                            (invoice.selectedServices && Object.values(invoice.selectedServices).some((service: any) =>
                                service.type?.toLowerCase().includes('solar') ||
                                service.name?.toLowerCase().includes('solar')
                            )) && (
                                <div className="bg-light p-3 rounded-lg mt-4 mb-4">
                                    <h5 className="pb-2 mb-3">Selected Colors</h5>
                                    <Row>
                                        {invoice.meshColor && (
                                            <Col xs={12} md={6} className="mb-2">
                                                <strong>Mesh Color:</strong> {invoice.meshColor}
                                            </Col>
                                        )}
                                        {invoice.frameColor && (
                                            <Col xs={12} md={6} className="mb-2">
                                                <strong>Frame Color:</strong> {invoice.frameColor}
                                            </Col>
                                        )}
                                    </Row>
                                </div>
                            )}

                        {/* Payment Plan & Invoices Section */}
                        {((invoice.isEstimate && invoice.status === EstimateStatus.accepted && relatedInvoices.length > 0) ||
                            (!invoice.isEstimate && relatedInvoices.length > 0)) ? (
                            <div className="mb-4">
                                <h5 className="pb-2 mb-3">Payment Plan Details</h5>

                                {/* Payment Plan Summary */}
                                <div className="bg-light p-3 rounded-lg mb-4">
                                    <Row className="mb-2">
                                        <Col xs={12} md={6} className="mb-2">
                                            <strong>Payment Plan:</strong> {
                                                invoice.paymentPlan === 'months_12' ? '12 Monthly Payments' :
                                                    invoice.paymentPlan === 'months_6' ? '6 Monthly Payments' :
                                                        invoice.paymentPlan === 'months_3' ? '3 Monthly Payments' :
                                                            invoice.paymentPlan === 'half_now' ? '50% Now, 50% After Installation' :
                                                                'Standard Payment'
                                            }
                                        </Col>
                                        <Col xs={12} md={6} className="mb-2">
                                            <strong>Total Price:</strong>{" "}
                                            {invoice.pricingType !== "dimensions" ?
                                                `$${invoice.discount && invoice.discount > 0
                                                    ? ((getTotalPrice(invoice) * (1 - invoice.discount / 100))).toFixed(2)
                                                    : getTotalPrice(invoice).toFixed(2)}
                                                `
                                                :
                                                `$${(invoice.discountPercent && invoice.discountPercent > 0)
                                                    ? ((invoice.amount * (1 - (invoice.discountPercent || 0) / 100)) * (invoice.meshType === 80 ? .88 : 1)).toFixed(2)
                                                    : (invoice.amount * (invoice.meshType === 80 ? .88 : 1)).toFixed(2)}`
                                            }
                                        </Col>
                                    </Row>
                                </div>

                                {/* 
                                  * Payments List 
                                  * NOTE: When displaying multiple invoices for payment plans,
                                  * each invoice should show the ORIGINAL pricing in its line items,
                                  * not the discounted price. The discounted amount should only be
                                  * shown in the total section at the bottom of each invoice.
                                  */}
                                <Accordion defaultActiveKey={invoice.isEstimate ? undefined : "0"} className="mb-4">
                                    <Accordion.Item eventKey="0">
                                        <Accordion.Header>
                                            <span className="fw-bold">
                                                Payment Schedule
                                                <Badge className="ms-2" bg="primary">
                                                    {relatedInvoices.filter(inv => inv.status === InvoiceStatus.paid).length} of {relatedInvoices.length} Paid
                                                </Badge>
                                            </span>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className="list-group">
                                                {relatedInvoices.map((inv, index) => (
                                                    <div
                                                        key={inv.id}
                                                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${!invoice.isEstimate && invoiceId === inv.id ? 'active bg-primary' : ''}`}
                                                    >
                                                        <div>
                                                            <h6 className={`mb-0 ${!invoice.isEstimate && invoiceId === inv.id ? 'text-white' : ''}`}>
                                                                Payment {inv.paymentNumber} of {inv.totalPayments}
                                                                {inv.dueDate && (
                                                                    <small className={`ms-2 ${!invoice.isEstimate && invoiceId === inv.id ? 'text-white' : 'text-muted'}`}>
                                                                        (Due: {new Date(inv.dueDate.toDate()).toLocaleDateString()})
                                                                    </small>
                                                                )}
                                                            </h6>
                                                            <small>
                                                                {inv.status === InvoiceStatus.paid ? (
                                                                    <span className={`${!invoice.isEstimate && invoiceId === inv.id ? 'text-white' : 'text-success'}`}>
                                                                        <i className="fas fa-check-circle me-1"></i>
                                                                        Paid {inv.paymentDate && `on ${new Date(inv.paymentDate.toDate()).toLocaleDateString()}`}
                                                                    </span>
                                                                ) : inv.status === InvoiceStatus.pending ? (
                                                                    <span className={`${!invoice.isEstimate && invoiceId === inv.id ? 'text-white' : 'text-warning'}`}>
                                                                        <i className="fas fa-clock me-1"></i>
                                                                        Payment Due
                                                                    </span>
                                                                ) : (
                                                                    <span className={`${!invoice.isEstimate && invoiceId === inv.id ? 'text-white' : 'text-muted'}`}>
                                                                        <i className="fas fa-calendar-alt me-1"></i>
                                                                        Scheduled
                                                                    </span>
                                                                )}
                                                            </small>
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            <div className={`me-3 fw-bold ${!invoice.isEstimate && invoiceId === inv.id ? 'text-white' : ''}`}>${Math.round(inv.amount)}</div>
                                                            {inv.status !== InvoiceStatus.paid && (
                                                                <Button
                                                                    size="sm"
                                                                    variant={!invoice.isEstimate && invoiceId === inv.id ? "light" :
                                                                        inv.status === InvoiceStatus.pending ? "primary" : "outline-secondary"}
                                                                    onClick={() => navigate(`/invoice/${inv.id}`)}
                                                                >
                                                                    {inv.status === InvoiceStatus.pending ? "Pay Now" : "View"}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </div>
                        ) : null}

                        {/* Images Section */}
                        {!invoice.isEstimate && invoice.images && invoice.images.length > 0 && (
                            <div className="mt-4">
                                <div className="d-flex justify-content-between align-items-center pb-2 mb-3">
                                    <h5 className="mb-0">Attachments</h5>
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={async () => {
                                            // Download all images
                                            for (let i = 0; i < invoice.images!.length; i++) {
                                                const imagePath = invoice.images![i];
                                                try {
                                                    const imageService = new (await import('../services/InvoiceImageService')).default();
                                                    const downloadUrl = `${imageService.apiBaseUrl}/invoices/download/${imagePath}`;

                                                    const response = await fetch(downloadUrl);
                                                    if (!response.ok) continue;

                                                    const blob = await response.blob();
                                                    const url = window.URL.createObjectURL(blob);

                                                    const link = document.createElement('a');
                                                    link.href = url;
                                                    link.download = `invoice-${invoiceId}-image-${i + 1}.jpg`;
                                                    link.style.display = 'none';
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);

                                                    window.URL.revokeObjectURL(url);

                                                    // Add delay between downloads
                                                    await new Promise(resolve => setTimeout(resolve, 500));
                                                } catch (error) {
                                                    console.error('Error downloading image:', error);
                                                }
                                            }
                                        }}
                                    >
                                        <i className="fas fa-download me-1"></i>
                                        Download All
                                    </Button>
                                </div>
                                <Row>
                                    {invoice.images.map((imagePath, index) => (
                                        <Col key={index} xs={12} sm={6} md={4} lg={3} className="mb-3">
                                            <div className="rounded p-2">
                                                <ImagePreview imagePath={imagePath} />
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}

                        {/* Invoice/Estimate and Receipt Download Actions */}
                        {invoice.isEstimate && (
                            invoice.status === EstimateStatus.signed ||
                            invoice.signatureLocked ||
                            !!invoice.signature?.signedAtUTC
                        ) && (
                                <div className="text-center mt-4 mb-2">
                                    <span className="badge bg-success">
                                        Estimate Signed
                                    </span>
                                </div>
                            )}
                        <div className="d-flex justify-content-center mt-4" style={{ gap: '1rem' }}>
                            {/* Download Invoice Button - Only for invoices, not estimates */}
                            {!invoice.isEstimate && (
                                <Button
                                    variant="primary"
                                    size="lg"
                                    onClick={async () => {
                                        try {
                                            // Download invoice/estimate PDF
                                            const downloadUrl = `${API_BASE_URL}/invoices/${invoiceId}/download`;

                                            const response = await fetch(downloadUrl);
                                            if (!response.ok) {
                                                throw new Error(`Failed to download ${invoice.isEstimate ? 'estimate' : 'invoice'}`);
                                            }

                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);

                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = invoice.isEstimate ? `estimate-${invoiceId}.pdf` : `invoice-${invoiceId}.pdf`;
                                            link.style.display = 'none';
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);

                                            window.URL.revokeObjectURL(url);
                                        } catch (error) {
                                            console.error(`Error downloading ${invoice.isEstimate ? 'estimate' : 'invoice'}:`, error);
                                            alert(`Failed to download ${invoice.isEstimate ? 'estimate' : 'invoice'}. Please try again later.`);
                                        }
                                    }}
                                >
                                    <i className="fas fa-file-invoice me-2"></i>
                                    Download Invoice
                                </Button>
                            )}

                            {/* Receipt Actions - Only for Paid Invoices (not estimates) */}
                            {!invoice.isEstimate && (invoice.status === 'paid' || invoice.status === InvoiceStatus.paid) && (
                                <>
                                    <Button
                                        variant="success"
                                        size="lg"
                                        onClick={async () => {
                                            try {
                                                // Download receipt PDF
                                                const downloadUrl = `${API_BASE_URL}/invoices/${invoiceId}/receipt/download`;

                                                const response = await fetch(downloadUrl);
                                                if (!response.ok) {
                                                    throw new Error('Failed to download receipt');
                                                }

                                                const blob = await response.blob();
                                                const url = window.URL.createObjectURL(blob);

                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.download = `receipt-${invoiceId}.pdf`;
                                                link.style.display = 'none';
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);

                                                window.URL.revokeObjectURL(url);
                                            } catch (error) {
                                                console.error('Error downloading receipt:', error);
                                                alert('Failed to download receipt. Please try again later.');
                                            }
                                        }}
                                    >
                                        <i className="fas fa-download me-2"></i>
                                        Download Receipt
                                    </Button>

                                    <Button
                                        variant="outline-success"
                                        size="lg"
                                        onClick={async () => {
                                            const email = prompt('Enter email address to send receipt:');
                                            if (!email) return;

                                            try {
                                                const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/send-receipt`, {
                                                    method: 'POST',
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                    },
                                                    body: JSON.stringify({
                                                        email: email,
                                                        paymentIntent: ''  // Payment intent not available in invoice display
                                                    })
                                                });

                                                const data = await response.json();

                                                if (response.ok) {
                                                    alert(`Receipt sent successfully to ${email}`);
                                                } else {
                                                    throw new Error(data.error || 'Failed to send receipt');
                                                }
                                            } catch (error: any) {
                                                console.error('Error sending receipt:', error);
                                                alert(error.message || 'Failed to send receipt. Please try again later.');
                                            }
                                        }}
                                    >
                                        <i className="fas fa-envelope me-2"></i>
                                        Email Receipt
                                    </Button>
                                </>
                            )}

                            {/* Download Estimate Button - Always visible for estimates */}
                            {invoice.isEstimate && (
                                <Button
                                    variant="primary"
                                    size="lg"
                                    onClick={async () => {
                                        try {
                                            const downloadUrl = `${API_BASE_URL}/invoices/${invoiceId}/download`;
                                            const response = await fetch(downloadUrl);

                                            if (!response.ok) {
                                                throw new Error('Failed to download estimate');
                                            }

                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = `estimate-${invoiceId}.pdf`;
                                            link.style.display = 'none';
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            window.URL.revokeObjectURL(url);
                                        } catch (error) {
                                            console.error('Error downloading estimate:', error);
                                            alert('Failed to download estimate. Please try again later.');
                                        }
                                    }}
                                >
                                    <i className="fas fa-file-invoice me-2"></i>
                                    Download Estimate
                                </Button>
                            )}
                        </div>

                        {/* Tip Selection Section - Only show for unpaid invoices */}
                        {invoice.status !== InvoiceStatus.paid && !invoice.isEstimate && (
                            <div className="card border-0 shadow-sm mt-4">
                                <div className="card-body">
                                    <TipSelection
                                        subtotal={
                                            invoice.discountedTotal ||
                                            (invoice.discountPercent && invoice.discountPercent > 0
                                                ? invoice.amount * (1 - invoice.discountPercent / 100) * (invoice.meshType === 80 ? 0.88 : 1)
                                                : invoice.discountAmount && invoice.discountAmount > 0
                                                    ? (invoice.subtotal || invoice.amount || getTotalPrice(invoice)) - invoice.discountAmount
                                                    : invoice.amount || getTotalPrice(invoice)
                                            )
                                        }
                                        onTipChange={(amount, percent) => {
                                            setTipAmount(amount);
                                            setTipPercent(percent);
                                            // Update invoice with tip
                                            if (invoice) {
                                                const updatedInvoice: Invoice = {
                                                    ...invoice,
                                                    tipAmount: amount,
                                                    tipPercent: percent,
                                                    tipType: percent !== null ? 'percentage' : 'fixed'
                                                };
                                                setInvoice(updatedInvoice);
                                                props.setInvoice(updatedInvoice);
                                            }
                                        }}
                                        className="mb-3"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Total Price Section */}
                        <div className="card no-section-border shadow-sm mt-4">
                            <div className="card-body p-0">
                                <div className="p-3 modern-total-section">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="m-0 fw-bold text-dark">
                                            {!invoice.isEstimate && invoice.parentEstimateId ? "Next Payment Amount:" : "Total Due:"}
                                        </h5>
                                        <h3 className="text-dark fw-bold m-0 text-nowrap">
                                            {formatCurrency(
                                                typeof props.totalDue === 'number' && props.totalDue > 0
                                                    ? props.totalDue
                                                    : (invoice.discountedTotal || invoice.total || getTotalPrice(invoice)) + tipAmount
                                            )}
                                        </h3>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    <Col xs="12" className="my-3">
                        <div className="d-flex justify-content-center align-items-center p-5">
                            <div className="spinner-border text-primary me-3" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <h5 className="m-0">Loading...</h5>
                        </div>
                    </Col>
                </Row>
            )}
        </Row>
    );
};

export default DisplayInvoice;
